/**
 * Rule: an exclusive/inclusive gateway must not have two non-default outgoing
 * paths that share the SAME condition.
 *
 * On an exclusive gateway the first matching branch wins, so a second path with
 * an identical condition is permanently unreachable — a logic error. It arises
 * when the same branch is modelled twice (e.g. once as "Gateway → Onayla task"
 * and again as "Gateway → END", both `WHEN karar == "Onayla"`). gateway-conditions
 * only checks that each path HAS a condition; it doesn't compare them.
 *
 * Conditions live in the workflow JSON (`wf.nodes.decisions[].condition`), keyed
 * by flow id — same source gateway-conditions.js reads.
 *
 * Two producers write those conditions and they do NOT agree on a field name:
 * the symbol picker (manual editing) fills `fieldName`/`description` and leaves
 * `label` unset, while the workflow DSL writes `fieldName`/`label`/`description`
 * (before KOA-18448 it wrote `name` instead of `fieldName`, and that shape is
 * still on disk in every workflow it built back then).
 * Neither `name` nor `label` alone identifies a field, so identity is resolved
 * from whichever ISymbol field is actually populated — and when NONE is, the
 * condition is skipped rather than compared as an empty string. Guessing there
 * would flag two DIFFERENT fields compared against the same value (e.g.
 * `IsApproved == true` vs `OnayDurum == true`) as duplicates.
 */
const { is } = require("bpmnlint-utils");

// ConditionSetType.or — the model stores the group type here, not in isOrGroup.
const OR_SET_TYPE = 2;

// Operators flagged doesNotNeedSecondField in the condition editor: no field2.
const UNARY_OPERATORS = ["isempty", "isnotempty", "isguid"];

function getWF() {
  const w = typeof window !== "undefined" ? window : null;
  const inst = w && w.getSdInstance ? w.getSdInstance() : null;
  const prov = inst && inst.workflowProvider;
  const wf1 = prov && prov.getSelectedWf ? prov.getSelectedWf() : null;
  const wf2 = w && w.__wfSelected ? w.__wfSelected : null;
  return wf1 || wf2 || null;
}

/**
 * Only primitives carry a comparable identity — an object (a React label node,
 * a nested shape) would stringify to "[object Object]" and make two unrelated
 * symbols look equal.
 */
function primitiveText(value) {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

/**
 * What an ISymbol points at, as a comparable string. Ordered so that each
 * producer's own field is found first and both land on the same parameter name
 * (picker → fieldName, DSL → name). Returns null when nothing identifies it.
 * Exported for testing.
 */
function symbolIdentity(symbol) {
  if (!symbol || typeof symbol !== "object") return null;
  const candidates = [
    symbol.fieldName,
    symbol.name,
    symbol.diPropName,
    symbol.description,
    symbol.label,
    symbol.inputName
  ];
  for (const candidate of candidates) {
    const text = primitiveText(candidate);
    if (text !== "") return text;
  }
  return null;
}

/**
 * The right-hand side of a comparison: a literal (`fixed:true`) or a reference
 * to another symbol (`ref:Limit`). The prefixes keep the literal "Limit" apart
 * from the parameter Limit. Returns null when the value can't be resolved, so
 * the caller can stay silent instead of comparing two unknowns as equal.
 * Exported for testing.
 */
function symbolValue(symbol) {
  if (!symbol || typeof symbol !== "object") return null;

  if (symbol.isFixed === true || symbol.fixedValue !== undefined) {
    const fixed = symbol.fixedValue;
    if (fixed === undefined || fixed === null) return "fixed:";
    if (typeof fixed === "object") return null;
    return `fixed:${String(fixed)}`;
  }

  const identity = symbolIdentity(symbol);
  return identity === null ? null : `ref:${identity}`;
}

function isUnaryOperator(operator) {
  return UNARY_OPERATORS.includes(operator.toLowerCase());
}

/**
 * Canonical key for a decision's condition, so two paths with the same logical
 * condition compare equal regardless of object identity / entry order.
 * Returns null when the condition can't be canonicalized in full — a partially
 * resolved key would compare unrelated conditions as equal. Exported for testing.
 */
function conditionKey(decision) {
  const cond = decision && decision.condition;
  if (!cond || typeof cond !== "object") return null;

  let entries = [];
  const set = cond.conditionSet;
  if (set && Array.isArray(set.conditions) && set.conditions.length > 0) {
    entries = set.conditions;
  } else if (cond.field1 && cond.conditionOperator) {
    entries = [cond];
  }
  if (!entries.length) return null;

  const parts = [];
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") return null;

    // A nested group has no field1 of its own; identity resolution returns null
    // and the whole condition is skipped rather than half-compared.
    const field = symbolIdentity(entry.field1);
    if (field === null) return null;

    const operator = primitiveText(entry.conditionOperator);
    if (operator === "") return null;

    let value = "";
    if (!isUnaryOperator(operator)) {
      value = symbolValue(entry.field2);
      if (value === null) return null;
    }

    parts.push(`${field}|${operator}|${value}`);
  }

  parts.sort();
  const isOrGroup = cond.isOrGroup === true || (set && (set.type === OR_SET_TYPE || set.isOrGroup === true));
  return `${isOrGroup ? "OR" : "AND"}:${parts.join(",")}`;
}

/**
 * Given the gateway's outgoing flows (each {id, isDefault}), the workflow's
 * decisions array and the gateway's BPMN default flow id (when set), return the
 * ids of flows whose condition duplicates an earlier non-default sibling's.
 * Pure — exported for testing.
 */
function duplicateConditionFlowIds(flows, decisions, bpmnDefaultFlowId) {
  const seen = new Map(); // conditionKey → first flow id
  const dupes = [];
  for (const flow of flows) {
    const decision = decisions.find((d) => d && d.id === flow.id);

    // BPMN xml is the canonical source for defaults; fall back to the JSON flag
    // only when the gateway has no default set at all — same as gateway-conditions.
    let isDefault;
    if (bpmnDefaultFlowId) {
      isDefault = bpmnDefaultFlowId === flow.id;
    } else {
      isDefault = flow.isDefault === true || (decision && decision.isDefault === true);
    }
    if (isDefault) continue;

    const key = conditionKey(decision);
    if (key === null) continue; // missing / unresolvable condition is not our call
    if (seen.has(key)) {
      dupes.push(flow.id);
    } else {
      seen.set(key, flow.id);
    }
  }
  return dupes;
}

function gatewayDuplicateCondition() {
  return {
    check(node, reporter) {
      if (!node) return;
      if (!is(node, "bpmn:ExclusiveGateway") && !is(node, "bpmn:InclusiveGateway")) return;

      const outgoing = Array.isArray(node.outgoing) ? node.outgoing : [];
      if (outgoing.length < 2) return;

      const wf = getWF();
      const decisions = wf && wf.nodes && Array.isArray(wf.nodes.decisions) ? wf.nodes.decisions : [];

      const bpmnDefaultFlowId = node.default ? node.default.id : undefined;
      const flows = outgoing.map((flow) => ({
        id: flow.id,
        isDefault: bpmnDefaultFlowId ? bpmnDefaultFlowId === flow.id : false
      }));

      for (const dupId of duplicateConditionFlowIds(flows, decisions, bpmnDefaultFlowId)) {
        reporter.report(
          dupId,
          "Two gateway paths share the same condition — the second is unreachable on an exclusive gateway. Give each branch a distinct condition, or remove the duplicate path."
        );
      }
    }
  };
}

module.exports = gatewayDuplicateCondition;
module.exports.conditionKey = conditionKey;
module.exports.duplicateConditionFlowIds = duplicateConditionFlowIds;
module.exports.symbolIdentity = symbolIdentity;
module.exports.symbolValue = symbolValue;
